import {
    Box,
    Button,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { getBlockchainIdentities } from '../services/blockchainService'
import {
    getCoverageByPersonId,
    getEligibilityTransactions,
    getMembers,
} from '../services/memberService'
import { BlockchainIdentity } from '../services/models/blockchain-identity'
import { EligibilityTransactionLog } from '../services/models/eligibility'
import { DentalCoverage, Member } from '../services/models/member'
import { getUserById } from '../services/userService'
import { formatDate, formatDateRange } from '../utils'
import { DataPanel } from './DataPanel'
import EligibilityDetailsDialog from './dialogs/eligibility/EligibilityDetailsDialog'
import MemberDetailsDialog from './dialogs/eligibility/MemberDetailsDialog'

const PayerEligibilityTransactionsTable = (props: { [key: string]: any }) => {
    const [tableData, setTableData] = useState<ITableEntry[]>([])
    const [providerIdentities, setProviderIdentities] = useState<BlockchainIdentity[]>([])
    const [selectedProviderIdentity, setSelectedProviderIdentity] = useState('')
    const [page, setPage] = useState(0)
    const [recordCount, setRecordCount] = useState(1)
    const [rowsPerPage, setRowsPerPage] = useState(5)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [dialogData, setDialogData] = useState<EligibilityTransactionLog | null>(null)
    const [memberDialogOpen, setMemberDialogOpen] = useState(false)
    const [memberdialogData, setMemberDialogData] = useState<{
        member: Member
        coverage: DentalCoverage
    }>()
    const tableCellCss = { justifyContent: 'center' }
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
    }
    interface ITableEntry extends EligibilityTransactionLog {
        requestedBy: string
        providerName: string
        serviceDate: string
        coverageStatus: string
    }
    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(+event.target.value)
        setPage(0)
    }

    const getMemberAndCoverage = async (
        subscriberId: string,
        planYear: number,
    ): Promise<{ member: Member; coverage: DentalCoverage } | undefined> => {
        const {
            data: { data },
        } = await getMembers(planYear, page * rowsPerPage, rowsPerPage, true, subscriberId)
        if (data && data.length == 1) {
            const member = data[0]
            const response = await getCoverageByPersonId(member.id)
            let result: { member: Member; coverage: DentalCoverage } = {
                member: member,
                coverage: response?.data,
            }
            return result
        }
    }

    const getRequestedUserName = (name: string) => {
        if (name?.startsWith('0x')) {
            return `0x...${name?.substring(name.length - 5)}`
        }
        return name
    }
    const getCoverageText = (status: string) => {
        switch (status) {
            case 'COVERED':
                return 'Covered'
            case 'NOT_COVERED':
                return 'Not Covered'
            case 'INACTIVE':
                return 'Inactive'
            case 'NOT_FOUND':
                return 'Not Found'
            default:
                return 'Unknown'
        }
    }
    useEffect(() => {
        const fetchProviders = async () => {
            const { data } = await getBlockchainIdentities('provider')
            setProviderIdentities(data)
            setSelectedProviderIdentity(data?.[0].did)
            return data
        }
        const fetchEligibilityTransactions = async () => {
            const {
                data: { transactions, numberOfEntries },
            } = await getEligibilityTransactions(
                page * rowsPerPage,
                rowsPerPage,
                props.status === 'SUCCESS',
                selectedProviderIdentity,
            )
            const identitiesResponse = await fetchProviders()
            const _tableData: ITableEntry[] = await Promise.all(
                transactions?.map(async (entry: EligibilityTransactionLog) => {
                    const { data } = await getUserById(encodeURI(entry.requestingUserId ?? ''))
                    return {
                        ...entry,
                        requestedBy: data?.nickname ?? '',
                        providerName: identitiesResponse.find((i) => i.did === entry.sourceNode)
                            ?.orgName,
                        coverageStatus: getCoverageText(entry.responseContent.coverageStatus),
                    }
                }),
            )
            setTableData(_tableData ?? [])
            setRecordCount(numberOfEntries)
        }
        fetchEligibilityTransactions()
    }, [page, rowsPerPage])

    return (
        <>
            <>
                <DataPanel
                    content={
                        <>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    margin: 2,
                                    flexGrow: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        flexGrow: 1,
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        sx={{ alignItems: 'center', display: 'flex' }}
                                    >
                                        <Typography
                                            fontSize="medium"
                                            fontWeight="bold"
                                            marginRight={'10px'}
                                        >
                                            Provider
                                        </Typography>
                                        {providerIdentities && providerIdentities.length > 0 && (
                                            <TextField
                                                select
                                                value={selectedProviderIdentity}
                                                onChange={(e) =>
                                                    setSelectedProviderIdentity(e.target.value)
                                                }
                                            >
                                                {providerIdentities?.map((identity, i) => {
                                                    return (
                                                        <MenuItem
                                                            key={identity.interchangeId}
                                                            value={identity.did}
                                                        >
                                                            {identity.orgName}
                                                        </MenuItem>
                                                    )
                                                })}
                                            </TextField>
                                        )}
                                    </Stack>
                                </Box>
                            </Box>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Service Date</TableCell>
                                        <TableCell>Provider</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Requested On</TableCell>
                                        <TableCell>Requested By</TableCell>
                                        <TableCell>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tableData.map((entry: ITableEntry) => (
                                        <TableRow key={entry.id}>
                                            <TableCell>{entry.name}</TableCell>
                                            <TableCell>
                                                {formatDateRange(entry.serviceDate ?? '')}
                                            </TableCell>
                                            <TableCell>{entry.providerName}</TableCell>
                                            <TableCell>{entry.coverageStatus}</TableCell>
                                            <TableCell>
                                                {formatDate(entry.createdOn, 'MM/DD/YYYY')}
                                            </TableCell>
                                            <TableCell>
                                                {getRequestedUserName(entry.requestedBy)}
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    sx={{ borderRadius: 5 }}
                                                    onClick={() => {
                                                        setDialogData(entry)
                                                        setDialogOpen(true)
                                                    }}
                                                >
                                                    Details
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </>
                    }
                />
            </>
            <>
                <Box
                    sx={{
                        paddingTop: 1,
                        marginLeft: 2,
                        marginRight: 2,
                        alignItems: 'center',
                        display: 'flex',
                    }}
                >
                    <Box sx={{ flexGrow: 1 }}></Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 15, 20, 50, 100]}
                            component="div"
                            count={recordCount}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Box>
                </Box>
            </>
            {
                <EligibilityDetailsDialog
                    open={dialogOpen}
                    currentNode="payer"
                    onClose={() => setDialogOpen(false)}
                    onMemberClick={async (subscriberId: string, planYear: number) => {
                        if (subscriberId && planYear) {
                            const response = await getMemberAndCoverage(subscriberId, planYear)
                            if (response) {
                                setMemberDialogData(response)
                                setMemberDialogOpen(true)
                            }
                        }
                    }}
                    data={dialogData as EligibilityTransactionLog}
                />
            }
            {
                <MemberDetailsDialog
                    open={memberDialogOpen}
                    onClose={() => {
                        setMemberDialogOpen(false)
                    }}
                    data={memberdialogData}
                />
            }
        </>
    )
}

export default PayerEligibilityTransactionsTable
